import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProjectionContext } from '@/services/inventorySimulationService';
import { formatQty } from '../utils/projection';

interface SimulationParamsPanelProps {
  context: ProjectionContext;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-[180px] flex-1 rounded-md border border-gray-100 bg-gray-50/60 px-3 py-2">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Item({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] text-gray-500">{label}</span>
      <span
        className={`text-xs tabular-nums ${strong ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}
      >
        {value}
      </span>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const [y, m, d] = value.split('-');
  if (!y || !m || !d) return value;
  return `${d}/${m}/${y}`;
}

function formatDays(value: number | null): string {
  return value === null || value === undefined ? '—' : `${value} d`;
}

const METHOD_STYLES: Record<string, string> = {
  DAYS_COVERAGE: 'bg-sky-100 text-sky-800',
  MRP: 'bg-purple-100 text-purple-800',
  DDMRP: 'bg-teal-100 text-teal-800',
  FORECAST_BASED: 'bg-indigo-100 text-indigo-800',
  PURCHASE_TO_ORDER: 'bg-orange-100 text-orange-800',
};

function MethodBadge({ method }: { method: string }) {
  const { t } = useTranslation();
  if (!method) return null;
  const style = METHOD_STYLES[method] ?? 'bg-gray-100 text-gray-700';
  const label = t(`inventorySimulation.method.${method}`, { defaultValue: method });
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-medium ${style}`}>
      {label}
    </span>
  );
}

export function SimulationParamsPanel({ context }: SimulationParamsPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-800">
          {t('inventorySimulation.params.title')}
        </h3>
        <MethodBadge method={context.calculation_method} />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Section title={t('inventorySimulation.params.sectionPeriods')}>
          <Item label={t('inventorySimulation.params.leadTime')} value={formatDays(context.lead_time_days)} />
          <Item label={t('inventorySimulation.params.reviewPeriod')} value={formatDays(context.review_period_days)} />
          <Item label={t('inventorySimulation.params.protectionPeriod')} value={formatDays(context.protection_period_days)} strong />
          <Item
            label={t('inventorySimulation.params.daysOfSupply')}
            value={formatDays(context.days_of_supply === null ? null : Math.round(Number(context.days_of_supply)))}
            strong
          />
        </Section>

        <Section title={t('inventorySimulation.params.sectionReplenishment')}>
          <Item label={t('inventorySimulation.params.reorderPoint')} value={formatQty(context.reorder_point)} />
          <p className="pt-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            {t('inventorySimulation.params.safetyStockTitle')}
          </p>
          <div className="space-y-1 border-l-2 border-gray-200 pl-2">
            <Item label={t('inventorySimulation.params.safetyStockTotal')} value={formatQty(context.safety_stock)} strong />
            <Item label={t('inventorySimulation.params.internalSafetyStock')} value={formatQty(context.internal_safety_stock)} />
            <Item label={t('inventorySimulation.params.supplierSafetyStock')} value={formatQty(context.supplier_safety_stock)} />
          </div>
          <Item label={t('inventorySimulation.params.suggestedQuantity')} value={formatQty(context.final_suggested_quantity)} strong />
        </Section>

        <Section title={t('inventorySimulation.params.sectionRisk')}>
          <Item label={t('inventorySimulation.params.firstBelowSafety')} value={formatDate(context.first_below_safety_date)} />
          <Item label={t('inventorySimulation.params.firstStockout')} value={formatDate(context.first_stockout_date)} strong />
        </Section>
      </div>
    </div>
  );
}
